import { useState, useCallback } from 'react'
import {
  generatePropReference,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage prop reference generation and polling
 * Handles prop map generation, editing, and batch updates
 */
export const usePropReferences = ({ formData, setFormData } = {}) => {
  const [isGeneratingPropReference, setIsGeneratingPropReference] = useState(
    false
  )
  const [propGenerationProgress, setPropGenerationProgress] = useState(0)
  const [propGenerationStatus, setPropGenerationStatus] = useState('')

  /**
   * Poll prop task status until completion
   */
  const pollPropTaskStatus = useCallback(
    async taskId => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            setFormData(prev => ({
              ...prev,
              props_reference: response.data.output
            }))
            setIsGeneratingPropReference(false)
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Prop reference task failed:', response.data)
            setIsGeneratingPropReference(false)
            break
          } else {
            setPropGenerationProgress(
              response.data && response.data.progress
                ? response.data.progress
                : propGenerationProgress
            )
            setPropGenerationStatus(
              response.data && response.data.status ? response.data.status : ''
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [setFormData, propGenerationProgress]
  )

  /**
   * Poll add/edit prop task status
   */
  const pollAddPropTaskStatus = useCallback(
    async (taskId, newPropMap) => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...response.data.output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...newPropMap
              }
            }))
            setIsGeneratingPropReference(false)
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Add prop reference task failed:', response.data)
            setIsGeneratingPropReference(false)
            break
          } else {
            setPropGenerationProgress(
              response.data && response.data.progress
                ? response.data.progress
                : propGenerationProgress
            )
            setPropGenerationStatus(
              response.data && response.data.status ? response.data.status : ''
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [setFormData, propGenerationProgress]
  )

  /**
   * Generate prop map references
   */
  const generatePropMap = useCallback(
    async (propMapObj, existingUris) => {
      setIsGeneratingPropReference(true)

      try {
        const propRes = await generatePropReference({
          prop_map_with_prompts: propMapObj,
          existing_prop_uris:
            existingUris !== undefined
              ? existingUris
              : formData?.props_reference
        })
        const taskId = propRes && propRes.data && propRes.data.task_id
        if (taskId) {
          await pollPropTaskStatus(taskId)
        } else {
          setIsGeneratingPropReference(false)
        }
      } catch (error) {
        console.error('Error generating prop map:', error)
        setIsGeneratingPropReference(false)
      }
    },
    [formData?.props_reference, pollPropTaskStatus]
  )

  /**
   * Add new prop reference
   */
  const handleAddPropReference = useCallback(
    async newPropMap => {
      setIsGeneratingPropReference(true)

      try {
        const propRes = await generatePropReference({
          prop_map_with_prompts: {
            ...(formData?.propMap || {}),
            ...newPropMap
          },
          existing_prop_uris: formData?.props_reference
        })
        const taskId = propRes && propRes.data && propRes.data.task_id
        if (taskId) {
          await pollAddPropTaskStatus(taskId, newPropMap)
        } else {
          setIsGeneratingPropReference(false)
        }
      } catch (error) {
        console.error('Error adding prop reference:', error)
        setIsGeneratingPropReference(false)
      }
    },
    [formData?.propMap, formData?.props_reference, pollAddPropTaskStatus]
  )

  /**
   * Edit existing prop reference
   */
  const handleEditPropReference = useCallback(
    async editingPropPrompt => {
      setIsGeneratingPropReference(true)

      try {
        const editingKeys = Object.keys(editingPropPrompt)

        // Remove old URIs for edited props
        const filteredPropUris = Object.keys(
          formData?.props_reference || {}
        ).reduce((acc, key) => {
          if (!editingKeys.includes(key)) {
            acc[key] = formData.props_reference[key]
          }
          return acc
        }, {})

        const propRes = await generatePropReference({
          prop_map_with_prompts: {
            ...(formData?.propMap || {}),
            ...editingPropPrompt
          },
          existing_prop_uris: filteredPropUris
        })

        const taskId = propRes?.data?.task_id

        if (taskId) {
          await pollAddPropTaskStatus(taskId, editingPropPrompt)
        } else {
          setIsGeneratingPropReference(false)
        }
      } catch (error) {
        console.error('Error editing prop reference:', error)
        setIsGeneratingPropReference(false)
      }
    },
    [formData?.propMap, formData?.props_reference, pollAddPropTaskStatus]
  )

  /**
   * Batch update multiple props
   */
  const handleBatchUpdateProps = useCallback(
    async (updatedPropMap, modifiedKeys) => {
      setIsGeneratingPropReference(true)

      try {
        // Filter out URIs for modified keys
        const filteredPropUris = Object.keys(
          formData?.props_reference || {}
        ).reduce((acc, key) => {
          if (!modifiedKeys.includes(key)) {
            acc[key] = formData.props_reference[key]
          }
          return acc
        }, {})

        const propRes = await generatePropReference({
          prop_map_with_prompts: updatedPropMap,
          existing_prop_uris: filteredPropUris
        })

        const taskId = propRes?.data?.task_id

        if (taskId) {
          await pollAddPropTaskStatus(taskId, updatedPropMap)
        } else {
          setIsGeneratingPropReference(false)
        }
      } catch (error) {
        console.error('Error batch updating prop references:', error)
        setIsGeneratingPropReference(false)
      }
    },
    [formData?.props_reference, pollAddPropTaskStatus]
  )

  /**
   * Reset prop generation state
   */
  const resetPropGeneration = useCallback(() => {
    setIsGeneratingPropReference(false)
    setPropGenerationProgress(0)
    setPropGenerationStatus('')
  }, [])

  return {
    // State
    isGeneratingPropReference,
    propGenerationProgress,
    propGenerationStatus,

    // Actions
    generatePropMap,
    handleAddPropReference,
    handleEditPropReference,
    handleBatchUpdateProps,
    resetPropGeneration
  }
}

export default usePropReferences
